import type { ClientDto } from '@schwrz/shared';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useCheckInsStore } from '../../stores/checkIns';
import { useClientsStore } from '../../stores/clients';
import { useCompanyNotesStore } from '../../stores/companyNotes';
import { useProjectsStore } from '../../stores/projects';
import { useTasksStore } from '../../stores/tasks';

import { COMPANY_SECTION_LABELS, type BreadcrumbItem } from './appBreadcrumbs';
import { companyDetailsParam, companyDetailsTo, companyProjectsSectionTo } from './company-route';

const COMPANY_FLOW_ROUTES = new Set([
  'company-details',
  'company-details-legacy',
  'company-projects-section',
  'company-projects-section-legacy',
  'company-notes-section',
  'company-note-details',
  'company-checkin-details',
]);

export function useAppBreadcrumbs() {
  const route = useRoute();
  const router = useRouter();
  const clientsStore = useClientsStore();
  const projectsStore = useProjectsStore();
  const tasksStore = useTasksStore();
  const notesStore = useCompanyNotesStore();
  const checkInsStore = useCheckInsStore();

  const dataTick = ref(0);

  function companyFlowRoot(): BreadcrumbItem {
    return { label: 'Компании', to: { name: 'companies' } };
  }

  function companyClientCrumb(client: ClientDto): BreadcrumbItem {
    return {
      label: client.name,
      to: companyDetailsTo(client),
    };
  }

  function companySectionCrumb(
    client: ClientDto,
    section: keyof typeof COMPANY_SECTION_LABELS,
  ): BreadcrumbItem {
    if (section === 'projects') {
      const listed = projectsStore.ofClient(client.id);
      const first = listed.find((p) => !p.archived) ?? listed[0];
      if (first) {
        return {
          label: COMPANY_SECTION_LABELS.projects,
          to: companyProjectsSectionTo(client, { project: first }),
        };
      }
      return {
        label: COMPANY_SECTION_LABELS.projects,
        to: companyDetailsTo(client),
      };
    }

    const routeBySection = {
      projects: 'company-projects-section',
      notes: 'company-notes-section',
      checkins: 'company-details',
    } as const;
    const name = routeBySection[section];
    const id = companyDetailsParam(client);
    return {
      label: COMPANY_SECTION_LABELS[section],
      to:
        section === 'checkins'
          ? { name, params: { id }, query: { focus: 'checkins' } }
          : { name, params: { id } },
    };
  }

  function clientFromRouteParam(): ClientDto | undefined {
    const ref = String(route.params['id']);
    return clientsStore.getByRef(ref);
  }

  function clientFromCompanyRouteParam(): ClientDto | undefined {
    const ref = String(route.params['companyId'] ?? route.params['id'] ?? '');
    return clientsStore.getByRef(ref);
  }

  async function ensureData(): Promise<void> {
    const name = route.name;
    if (typeof name !== 'string') return;

    const needsClients =
      COMPANY_FLOW_ROUTES.has(name) ||
      name === 'task-details' ||
      name === 'task-details-legacy';
    if (needsClients) {
      await Promise.all([clientsStore.load(), projectsStore.load()]);
    }

    if (name === 'task-details' || name === 'task-details-legacy') {
      const taskRef =
        name === 'task-details-legacy'
          ? String(route.params['id'])
          : String(route.params['taskId']);
      if (!tasksStore.getCachedByRef(taskRef)) {
        try {
          await tasksStore.fetchOne(taskRef);
        } catch {
          /* page shows its own error */
        }
      }
    }

    if (name === 'company-note-details') {
      const noteId = String(route.params['noteId']);
      if (!notesStore.byId.get(noteId)) {
        try {
          await notesStore.fetchOne(noteId);
        } catch {
          /* page shows its own error */
        }
      }
    }

    if (name === 'company-notes-section') {
      const client = clientFromRouteParam();
      if (client) await notesStore.loadForClient(client.id);
    }

    if (name === 'company-checkin-details') {
      const ruleId = String(route.params['ruleId']);
      if (!checkInsStore.ruleById.get(ruleId)) {
        try {
          await checkInsStore.fetchOne(ruleId);
        } catch {
          /* page shows its own error */
        }
      }
    }

    dataTick.value += 1;
  }

  watch(
    () => route.fullPath,
    () => {
      void ensureData();
    },
    { immediate: true },
  );

  const items = computed((): BreadcrumbItem[] => {
    void dataTick.value;
    const name = route.name;
    if (typeof name !== 'string') return [];

    if (name === 'company-details' || name === 'company-details-legacy') {
      const client = clientFromCompanyRouteParam();
      if (!client) return [];
      return [companyFlowRoot(), { label: client.name }];
    }

    if (name === 'company-projects-section' || name === 'company-projects-section-legacy') {
      const client = clientFromCompanyRouteParam();
      if (!client) return [];
      const projectRef =
        name === 'company-projects-section-legacy'
          ? route.query['project']
          : route.params['projectId'];
      const project =
        typeof projectRef === 'string'
          ? projectsStore.getByRef(projectRef)
          : projectsStore.ofClient(client.id).find((p) => !p.archived) ??
            projectsStore.ofClient(client.id)[0];
      return [
        companyFlowRoot(),
        companyClientCrumb(client),
        { label: project?.name ?? COMPANY_SECTION_LABELS.projects },
      ];
    }

    if (name === 'company-notes-section') {
      const client = clientFromRouteParam();
      if (!client) return [];
      return [
        companyFlowRoot(),
        companyClientCrumb(client),
        { label: COMPANY_SECTION_LABELS.notes },
      ];
    }

    if (name === 'company-note-details') {
      const noteId = String(route.params['noteId']);
      const client = clientFromRouteParam();
      const note = notesStore.byId.get(noteId);
      if (!client) return [];
      return [
        companyFlowRoot(),
        companyClientCrumb(client),
        companySectionCrumb(client, 'notes'),
        { label: note?.title || 'Заметка' },
      ];
    }

    if (name === 'company-checkin-details') {
      const ruleId = String(route.params['ruleId']);
      const client = clientFromRouteParam();
      const rule = checkInsStore.ruleById.get(ruleId);
      if (!client) return [];
      return [
        companyFlowRoot(),
        companyClientCrumb(client),
        companySectionCrumb(client, 'checkins'),
        { label: rule?.question || 'Опрос' },
      ];
    }

    if (name === 'task-details' || name === 'task-details-legacy') {
      const taskRef =
        name === 'task-details-legacy'
          ? String(route.params['id'])
          : String(route.params['taskId']);
      const task = tasksStore.getCachedByRef(taskRef);
      if (!task) return [];
      const project = projectsStore.items.find((p) => p.id === task.projectId);
      const client = project
        ? clientsStore.items.find((c) => c.id === project.clientId)
        : null;
      if (!project || !client) return [];

      const taskLabel = task.title || 'Задача';

      return [
        companyFlowRoot(),
        companyClientCrumb(client),
        {
          label: project.name,
          to: companyProjectsSectionTo(client, { project }),
        },
        { label: taskLabel },
      ];
    }

    return [];
  });

  const visible = computed(() => items.value.length > 1);

  function navigate(item: BreadcrumbItem): void {
    if (item.onClick) {
      item.onClick();
      return;
    }
    if (item.to) void router.push(item.to);
  }

  return { items, visible, navigate };
}
